import { useState, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, ArrowRight, Check, Brain, Activity, Heart, Pill,
  Sparkles, Loader2, FileSignature, User, Target, Stethoscope,
  ClipboardList, Edit3, X, AlertTriangle, Flag, BarChart3, TrendingUp,
  Gauge, PenTool, Save, XCircle,
} from 'lucide-react';
import { interraiSteps, wizardStepsMeta } from '@/data/interraiQuestions';
import { paiTemplate } from '@/data/mockData';
import { toast } from 'sonner';
import PaiManualCompilation from '@/components/wizard/PaiManualCompilation';
import PatientPanelHeader from '@/components/PatientPanelHeader';

const iconMap = { Brain, Heart, Activity, Pill, FileSignature };

/* ═══ AI Generation Steps ═══ */
const aiGenerationSteps = [
  { label: 'Analisi dati interRAI HC...', duration: 800 },
  { label: 'Elaborazione profilo clinico...', duration: 700 },
  { label: 'Generazione obiettivi di salute...', duration: 600 },
  { label: 'Calcolo prestazioni suggerite...', duration: 500 },
  { label: 'Ottimizzazione piano assistenziale...', duration: 400 },
];

/* ═══ Score Calculator (Mock) ═══ */
function calcScores(formData) {
  const val = (key) => parseInt(formData[key] ?? '0') || 0;
  // CPS: Cognitive Performance Scale (0-6) from C1, C2A, D1
  const c1 = val('cognitivo_C1');
  const c2a = val('cognitivo_C2A');
  const d1 = val('cognitivo_D1');
  const cps = Math.min(6, Math.round((c1 * 1.2 + c2a * 1.5 + d1 * 0.8)));
  // ADL: from G2 questions
  const adlKeys = ['funzione_fisica_G2A', 'funzione_fisica_G2B', 'funzione_fisica_G2E', 'funzione_fisica_G2G', 'funzione_fisica_G2I', 'funzione_fisica_G2J'];
  const adlSum = adlKeys.reduce((s, k) => s + val(k), 0);
  const adlMax = adlKeys.length * 6;
  const adlScore = Math.min(6, Math.round((adlSum / adlMax) * 6));
  // DRS: Depression Rating Scale from E1 questions
  const drsKeys = ['umore_E1A', 'umore_E1B', 'umore_E1C', 'umore_E1F', 'umore_E1G', 'umore_E1I'];
  const drsSum = drsKeys.reduce((s, k) => s + val(k), 0);
  const drs = Math.min(14, drsSum);
  // CHESS: from J items
  const j1 = val('condizioni_J1');
  const j6a = val('condizioni_J6A');
  const j7a = val('condizioni_J7A');
  const chess = Math.min(5, Math.round((j1 + j6a + j7a) * 0.8));
  // Pain scale
  const pain = val('condizioni_J6B');
  // RUG-III classification
  let rugClass = 'Fisicamente Complesso';
  if (chess >= 3 || j7a >= 1) rugClass = 'Clinicamente Complesso';
  else if (adlScore >= 4) rugClass = 'Deficit Funzionale Esteso';
  else if (cps >= 3) rugClass = 'Compromissione Cognitiva';
  else if (adlScore <= 1 && cps <= 1) rugClass = 'Funzioni Ridotte';
  return { cps, adl: adlScore, drs, chess, pain, rugClass };
}

/* ═══ Score Gauge ═══ */
function ScoreGauge({ label, value, max, desc, color = 'primary' }) {
  const pct = Math.round((value / max) * 100);
  const colorMap = {
    primary: { ring: 'text-primary', bg: 'bg-primary/10', fill: 'stroke-primary' },
    warning: { ring: 'text-warning', bg: 'bg-warning/10', fill: 'stroke-warning' },
    destructive: { ring: 'text-destructive', bg: 'bg-destructive/10', fill: 'stroke-destructive' },
    success: { ring: 'text-success', bg: 'bg-success/10', fill: 'stroke-success' },
  };
  const c = colorMap[color] || colorMap.primary;
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="flex flex-col items-center gap-2 p-3" data-testid={`gauge-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="relative w-24 h-24">
        <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={radius} fill="none" stroke="hsl(var(--border))" strokeWidth="6" />
          <circle cx="40" cy="40" r={radius} fill="none" className={c.fill} strokeWidth="6" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-xl font-black ${c.ring}`}>{value}<span className="text-xs font-normal text-muted-foreground">/{max}</span></span>
        </div>
      </div>
      <p className="text-xs font-bold text-foreground text-center">{label}</p>
      <p className="text-[10px] text-muted-foreground text-center leading-tight">{desc}</p>
    </div>
  );
}

/* ═══ Wireframe Body Map — Gender-conditional + CSS Hotspots ═══ */
const severityGlowColors = {
  0: { glow: '#22D3EE', label: 'Normale' },
  1: { glow: '#FDE047', label: 'Lieve' },
  2: { glow: '#FB923C', label: 'Moderato' },
  3: { glow: '#F87171', label: 'Grave' },
};

const MALE_IMG = 'https://static.prod-images.emergentagent.com/jobs/3eb3bdb2-35bc-407a-b777-49401522e3fa/images/2b317436e54be4e681ba059309e41dea6d4bb7744f0e609248c77b1e232c7326.png';
const FEMALE_IMG = 'https://static.prod-images.emergentagent.com/jobs/a3586fe9-3342-43e1-bb52-d5f9cf18d22a/images/ccca890ad80f8a22a8b6af7a6c6419dcca277b4c4115e410d8f494600f53b022.png';

/* Hotspot positions — CSS percentage coordinates on the image (top/left) */
const maleHotspots = [
  { id: 'male_head',       top: '10%', left: '50%' },
  { id: 'male_chest',      top: '27%', left: '50%' },
  { id: 'male_abdomen',    top: '37%', left: '50%' },
  { id: 'male_leftArm',    top: '35%', left: '32%' },
  { id: 'male_rightArm',   top: '35%', left: '68%' },
  { id: 'male_rightKnee',  top: '64%', left: '57%' },
  { id: 'male_rightAnkle', top: '84%', left: '56%' },
  { id: 'male_leftLeg',    top: '70%', left: '43%' },
];

const femaleHotspots = [
  { id: 'female_head',      top: '10%', left: '50%' },
  { id: 'female_neck',      top: '17%', left: '50%' },
  { id: 'female_shoulders', top: '21%', left: '36%' },
  { id: 'female_chest',     top: '28%', left: '50%' },
  { id: 'female_abdomen',   top: '38%', left: '50%' },
  { id: 'female_leftLeg',   top: '66%', left: '43%' },
  { id: 'female_rightLeg',  top: '66%', left: '57%' },
  { id: 'female_skin',      top: '92%', left: '50%' },
];

function WireframeBodyMap({ areas, patient }) {
  const [hoveredId, setHoveredId] = useState(null);
  const getArea = (id) => areas.find(a => a.id === id);

  const isFemale = patient?.sesso === 'F';
  const hotspots = isFemale ? femaleHotspots : maleHotspots;
  const imgSrc = isFemale ? FEMALE_IMG : MALE_IMG;
  const imgAlt = isFemale ? 'Modello anatomico femminile' : 'Modello anatomico maschile';

  return (
    <div className="relative" data-testid="wireframe-body-map">
      <div className="bg-[#060A18] rounded-2xl p-4 sm:p-5 relative overflow-hidden border border-cyan-500/10">
        {/* Subtle grid background */}
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(#00B4FF 1px, transparent 1px), linear-gradient(90deg, #00B4FF 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} />

        {/* Scan line */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-400/15 to-transparent absolute animate-pulse" style={{ top: '35%' }} />
        </div>

        {/* Single centered model with hotspot layer */}
        <div className="relative mx-auto" style={{ maxWidth: '320px', aspectRatio: '2 / 3' }}>
          <img
            src={imgSrc}
            alt={imgAlt}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
            draggable={false}
            data-testid="body-model-image"
          />

          {/* CSS hotspot dots — only for severity > 0 */}
          {hotspots.map(spot => {
            const area = getArea(spot.id);
            const sev = area?.severity || 0;
            if (sev === 0) return null;
            const colors = severityGlowColors[sev];
            const isHovered = hoveredId === spot.id;

            return (
              <div
                key={spot.id}
                className="absolute z-10"
                style={{ top: spot.top, left: spot.left, transform: 'translate(-50%, -50%)' }}
                onMouseEnter={() => setHoveredId(spot.id)}
                onMouseLeave={() => setHoveredId(null)}
                data-testid={`body-zone-${spot.id}`}
              >
                {/* Outer pulse ring */}
                <div
                  className="absolute rounded-full animate-ping"
                  style={{
                    width: '24px', height: '24px',
                    top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: colors.glow,
                    opacity: 0.25,
                  }}
                />
                {/* Inner glowing dot */}
                <div
                  className="relative w-3.5 h-3.5 rounded-full cursor-pointer transition-transform duration-200"
                  style={{
                    backgroundColor: colors.glow,
                    boxShadow: `0 0 8px ${colors.glow}, 0 0 20px ${colors.glow}90, 0 0 40px ${colors.glow}50`,
                    transform: isHovered ? 'scale(1.5)' : 'scale(1)',
                  }}
                />

                {/* Tooltip on hover */}
                {isHovered && (
                  <div
                    className="absolute z-30 pointer-events-none"
                    style={{
                      bottom: '100%', left: '50%',
                      transform: 'translateX(-50%)',
                      marginBottom: '12px',
                    }}
                  >
                    <div
                      className="px-3.5 py-2.5 rounded-xl border text-center whitespace-nowrap backdrop-blur-lg"
                      style={{
                        background: 'rgba(6,10,24,0.95)',
                        borderColor: colors.glow,
                        boxShadow: `0 0 14px ${colors.glow}50, 0 0 30px ${colors.glow}20`,
                      }}
                      data-testid="body-tooltip"
                    >
                      <p className="text-[10px] font-extrabold tracking-wider uppercase" style={{ color: colors.glow }}>
                        {colors.label}
                      </p>
                      <p className="text-[12px] font-bold text-white mt-0.5">{area.label}</p>
                      <p className="text-[10px] text-blue-200/70 mt-1 leading-relaxed whitespace-normal max-w-[200px]">{area.note}</p>
                    </div>
                    {/* Arrow */}
                    <div className="mx-auto w-0 h-0" style={{
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: `6px solid ${colors.glow}`,
                    }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-4 mt-4 pt-3 border-t border-cyan-500/10">
          {Object.entries(severityGlowColors).filter(([sev]) => parseInt(sev) > 0).map(([sev, config]) => (
            <div key={sev} className="flex items-center gap-1.5">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{
                  backgroundColor: config.glow,
                  boxShadow: `0 0 6px ${config.glow}80, 0 0 14px ${config.glow}40`,
                }}
              />
              <span className="text-[10px] font-medium text-blue-200/50">{config.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══ Touch Option Button ═══ */
function TouchOption({ label, desc, selected, value, optionCount, onClick }) {
  const getSeverityStyle = () => {
    const v = parseInt(value);
    if (isNaN(v) || v === 8) return { bg: 'bg-muted/60 border-border', active: 'bg-muted border-foreground/30 ring-2 ring-foreground/10', text: 'text-foreground', check: 'bg-muted-foreground' };
    if (v === 0) return { bg: 'bg-success/8 border-success/20', active: 'bg-success/15 border-success ring-2 ring-success/20', text: 'text-success', check: 'bg-success' };
    if (optionCount <= 3) {
      if (v === 1) return { bg: 'bg-warning/8 border-warning/20', active: 'bg-warning/15 border-warning ring-2 ring-warning/20', text: 'text-warning', check: 'bg-warning' };
      return { bg: 'bg-destructive/8 border-destructive/20', active: 'bg-destructive/15 border-destructive ring-2 ring-destructive/20', text: 'text-destructive', check: 'bg-destructive' };
    }
    const ratio = v / (optionCount - 1);
    if (ratio <= 0.33) return { bg: 'bg-success/6 border-success/15', active: 'bg-success/12 border-success ring-2 ring-success/15', text: 'text-success', check: 'bg-success' };
    if (ratio <= 0.66) return { bg: 'bg-warning/8 border-warning/20', active: 'bg-warning/15 border-warning ring-2 ring-warning/20', text: 'text-warning', check: 'bg-warning' };
    return { bg: 'bg-destructive/8 border-destructive/20', active: 'bg-destructive/15 border-destructive ring-2 ring-destructive/20', text: 'text-destructive', check: 'bg-destructive' };
  };
  const s = getSeverityStyle();
  return (
    <button onClick={onClick} data-testid={`option-${value}`} className={`w-full text-left rounded-xl border-2 px-4 py-3.5 transition-all duration-200 active:scale-[0.98] ${selected ? s.active : `${s.bg} hover:opacity-80`}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold ${selected ? s.text : 'text-foreground'}`}>{label}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
        </div>
        {selected && <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${s.check}`}><Check className="w-3.5 h-3.5 text-white" /></div>}
        <span className={`text-xl font-bold shrink-0 tabular-nums ${selected ? s.text : 'text-foreground/70'}`}>{value}</span>
      </div>
    </button>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN WIZARD COMPONENT
   Phases: questions → results → pai
   ═══════════════════════════════════════════════════ */
export default function EvaluationWizard({ patient, onBack, completeEvaluation, onSaveDraft, initialFormData, initialSectionIdx, initialQuestionIdx }) {
  const [phase, setPhase] = useState('questions'); // questions | results | pai
  const [currentSectionIdx, setCurrentSectionIdx] = useState(initialSectionIdx ?? 0);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(initialQuestionIdx ?? 0);
  const [formData, setFormData] = useState(initialFormData ?? {});
  const [showErrors, setShowErrors] = useState(false);
  const [isGeneratingPAI, setIsGeneratingPAI] = useState(false);
  const [paiGenerated, setPaiGenerated] = useState(false);
  const [aiStepIndex, setAiStepIndex] = useState(-1);
  const [aiProgress, setAiProgress] = useState(0);
  const [generatedPAI, setGeneratedPAI] = useState(null);
  const [isSigning, setIsSigning] = useState(false);
  const [notesPAI, setNotesPAI] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editPrestazioni, setEditPrestazioni] = useState([]);
  const [editObiettivi, setEditObiettivi] = useState([]);
  const [showInterruptConfirm, setShowInterruptConfirm] = useState(false);
  const autoAdvanceRef = useRef(null);

  const totalSections = interraiSteps.length;
  const section = interraiSteps[currentSectionIdx];
  const question = section?.domande[currentQuestionIdx];
  const totalQuestionsAll = interraiSteps.reduce((s, sec) => s + sec.domande.length, 0);
  const flatIdx = interraiSteps.slice(0, currentSectionIdx).reduce((s, sec) => s + sec.domande.length, 0) + currentQuestionIdx;
  const isVeryLast = currentSectionIdx === totalSections - 1 && currentQuestionIdx === section.domande.length - 1;

  const sectionAnswered = (si) => {
    const sec = interraiSteps[si];
    return sec.domande.filter(d => formData[`${sec.id}_${d.id}`] !== undefined).length;
  };

  const scores = useMemo(() => calcScores(formData), [formData]);

  /* ─── Navigation helpers ─── */
  const goNextQuestion = () => {
    if (currentQuestionIdx < section.domande.length - 1) {
      setCurrentQuestionIdx(prev => prev + 1);
    } else if (currentSectionIdx < totalSections - 1) {
      setCurrentSectionIdx(prev => prev + 1);
      setCurrentQuestionIdx(0);
    }
  };

  const goPrevQuestion = () => {
    if (currentQuestionIdx > 0) {
      setCurrentQuestionIdx(prev => prev - 1);
    } else if (currentSectionIdx > 0) {
      const prevSec = interraiSteps[currentSectionIdx - 1];
      setCurrentSectionIdx(prev => prev - 1);
      setCurrentQuestionIdx(prevSec.domande.length - 1);
    }
  };

  const handleAnswer = (sectionId, questionId, value) => {
    setFormData(prev => ({ ...prev, [`${sectionId}_${questionId}`]: value }));
    if (showErrors) setShowErrors(false);
    // Auto-advance after 350ms
    if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);
    autoAdvanceRef.current = setTimeout(() => goNextQuestion(), 350);
  };

  const handleFinishQuestions = () => {
    // Validate ALL questions across ALL sections
    const missing = [];
    interraiSteps.forEach(sec => {
      sec.domande.forEach(d => {
        if (formData[`${sec.id}_${d.id}`] === undefined) missing.push({ sectionIdx: interraiSteps.indexOf(sec), questionIdx: sec.domande.indexOf(d), id: d.id });
      });
    });
    if (missing.length > 0) {
      setShowErrors(true);
      const first = missing[0];
      setCurrentSectionIdx(first.sectionIdx);
      setCurrentQuestionIdx(first.questionIdx);
      toast.error('Completa tutti i campi', { description: `${missing.length} domand${missing.length === 1 ? 'a' : 'e'} senza risposta.` });
      return;
    }
    setShowErrors(false);
    setPhase('results');
    toast.success('Valutazione completata', { description: 'Punteggi calcolati. Procedi alla pianificazione.' });
  };

  const handleNext = () => {
    if (isVeryLast) {
      handleFinishQuestions();
    } else {
      goNextQuestion();
    }
  };

  const handleGeneratePAI = () => {
    setIsGeneratingPAI(true);
    setAiStepIndex(0);
    setAiProgress(0);
    let stepIdx = 0;
    const runStep = () => {
      if (stepIdx >= aiGenerationSteps.length) {
        setIsGeneratingPAI(false);
        setPaiGenerated(true);
        setAiStepIndex(-1);
        setAiProgress(100);
        setGeneratedPAI({ obiettivi: paiTemplate.obiettivi, prestazioni: paiTemplate.prestazioni, bodyAreas: paiTemplate.bodyAreas });
        toast.success('Bozza PAI generata con AI', { description: 'Rivedi e conferma il piano assistenziale.' });
        setPhase('pai');
        return;
      }
      setAiStepIndex(stepIdx);
      setAiProgress(Math.round(((stepIdx + 1) / aiGenerationSteps.length) * 100));
      stepIdx++;
      setTimeout(runStep, aiGenerationSteps[stepIdx - 1].duration);
    };
    runStep();
  };

  const handleSign = () => {
    setIsSigning(true);
    setTimeout(() => {
      completeEvaluation(patient.id);
      setIsSigning(false);
      toast.success('PAI firmato digitalmente', { description: 'Inviato al Case Manager.' });
      setTimeout(() => onBack(), 1500);
    }, 2000);
  };

  const enterEditMode = () => {
    setEditPrestazioni((generatedPAI?.prestazioni || []).map(p => ({ ...p })));
    setEditObiettivi((generatedPAI?.obiettivi || []).map(o => ({ ...o })));
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const saveEdits = () => {
    setGeneratedPAI(prev => ({ ...prev, prestazioni: editPrestazioni, obiettivi: editObiettivi }));
    setIsEditing(false);
    toast.success('Modifiche salvate', { description: 'Il PAI è stato aggiornato con le tue correzioni.' });
  };

  const updatePrestazione = (idx, field, value) => {
    setEditPrestazioni(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const updateObiettivo = (idx, field, value) => {
    setEditObiettivi(prev => prev.map((o, i) => i === idx ? { ...o, [field]: value } : o));
  };

  const handleInterrupt = () => {
    setShowInterruptConfirm(false);
    // Calculate completed sections
    let completedSections = 0;
    for (let i = 0; i < interraiSteps.length; i++) {
      const sec = interraiSteps[i];
      const answered = sec.domande.filter(d => formData[`${sec.id}_${d.id}`] !== undefined).length;
      if (answered === sec.domande.length) completedSections++;
      else break;
    }
    if (onSaveDraft) {
      onSaveDraft(formData, currentSectionIdx, currentQuestionIdx, completedSections);
    }
    toast.success('Bozza salvata', { description: 'Puoi riprendere la compilazione dalla sezione "Bozze in corso".' });
    onBack();
  };

  const handleSaveDraftQuick = () => {
    let completedSections = 0;
    for (let i = 0; i < interraiSteps.length; i++) {
      const sec = interraiSteps[i];
      const answered = sec.domande.filter(d => formData[`${sec.id}_${d.id}`] !== undefined).length;
      if (answered === sec.domande.length) completedSections++;
      else break;
    }
    if (onSaveDraft) {
      onSaveDraft(formData, currentSectionIdx, currentQuestionIdx, completedSections);
    }
    toast.success('Bozza salvata', { description: `${Object.keys(formData).length}/${totalQuestionsAll} risposte salvate.` });
  };

  const today = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

  /* ════════════════════════════════════════════
     PHASE: MANUAL PAI COMPILATION
     ════════════════════════════════════════════ */
  if (phase === 'manual') {
    return (
      <PaiManualCompilation
        patient={patient}
        onBack={() => setPhase('results')}
        completeEvaluation={completeEvaluation}
      />
    );
  }

  /* ════════════════════════════════════════════
     PHASE: RESULTS HUB
     ════════════════════════════════════════════ */
  if (phase === 'results') {
    return (
      <div className="flex flex-col h-[calc(100vh-3.5rem)]" data-testid="results-hub">
        {/* Header */}
        <div className="bg-background/95 backdrop-blur-sm border-b border-border px-4 sm:px-6 py-4 shrink-0">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={() => { setPhase('questions'); setCurrentSectionIdx(totalSections - 1); setCurrentQuestionIdx(interraiSteps[totalSections - 1].domande.length - 1); }} data-testid="results-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-foreground">Hub Esiti e Pianificazione</h1>
              <p className="text-xs text-muted-foreground">{patient.cognome} {patient.nome} &middot; {patient.eta} anni</p>
            </div>
            <Badge variant="chip" className="text-xs gap-1"><Check className="w-3 h-3" /> Valutazione completata</Badge>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
          <div className="max-w-5xl mx-auto space-y-6">
            <PatientPanelHeader patient={patient} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">

            {/* ── LEFT: Esito Valutazione ── */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col">
              <Card className="shadow-card h-full flex flex-col" data-testid="results-scores-card">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-bold">Esito Valutazione interRAI</CardTitle>
                        <p className="text-xs text-muted-foreground">Punteggi calcolati automaticamente</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400 px-2 py-1 rounded-md">{today}</span>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1">
                  <div className="grid grid-cols-3 gap-1 mb-4">
                    <ScoreGauge label="CPS" value={scores.cps} max={6} desc="Performance Cognitiva" color={scores.cps <= 2 ? 'success' : scores.cps <= 4 ? 'warning' : 'destructive'} />
                    <ScoreGauge label="ADL" value={scores.adl} max={6} desc="Autonomia Funzionale" color={scores.adl <= 2 ? 'success' : scores.adl <= 4 ? 'warning' : 'destructive'} />
                    <ScoreGauge label="DRS" value={scores.drs} max={14} desc="Scala Depressione" color={scores.drs <= 3 ? 'success' : scores.drs <= 8 ? 'warning' : 'destructive'} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-3 bg-muted/50 rounded-xl border border-border/50 text-center">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">CHESS</p>
                      <p className="text-2xl font-black text-foreground">{scores.chess}<span className="text-xs font-normal text-muted-foreground">/5</span></p>
                      <p className="text-[10px] text-muted-foreground">Stabilita Clinica</p>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-xl border border-border/50 text-center">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Dolore</p>
                      <p className="text-2xl font-black text-foreground">{scores.pain}<span className="text-xs font-normal text-muted-foreground">/4</span></p>
                      <p className="text-[10px] text-muted-foreground">Scala VAS</p>
                    </div>
                  </div>
                  <div className="p-3.5 bg-primary/5 rounded-xl border border-primary/15">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Classificazione RUG-III</p>
                    </div>
                    <p className="text-base font-black text-foreground" data-testid="rug-class">{scores.rugClass}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* ── RIGHT: Definizione PAI ── */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="flex flex-col">
              <Card className="shadow-card h-full flex flex-col" data-testid="results-pai-options-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-bold">Definizione Piano Assistenziale (PAI)</CardTitle>
                      <p className="text-xs text-muted-foreground">Scegli come procedere per obiettivi e interventi</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">

                  {/* Option A: AI */}
                  <button
                    onClick={handleGeneratePAI}
                    disabled={isGeneratingPAI}
                    className="w-full text-left p-5 rounded-2xl border-2 border-amber-300/50 bg-gradient-to-br from-amber-50/80 via-yellow-50/40 to-orange-50/30 dark:from-amber-950/30 dark:via-yellow-950/20 dark:to-orange-950/10 dark:border-amber-500/30 hover:shadow-lg hover:border-amber-400/70 transition-all duration-300 active:scale-[0.99] group"
                    data-testid="pai-option-ai"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0 shadow-md group-hover:shadow-lg transition-shadow">
                        <Sparkles className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-base font-black text-foreground">Genera Bozza PAI con AI</p>
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-[9px] font-bold border-0">Consigliato</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Analizza le risposte interRAI e proponi obiettivi e interventi compatibili con il profilo clinico.
                        </p>
                      </div>
                    </div>
                    {isGeneratingPAI && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4 p-4 bg-card rounded-xl border border-border">
                        <Progress value={aiProgress} className="h-2 mb-3" />
                        <div className="space-y-2">
                          {aiGenerationSteps.map((step, idx) => (
                            <div key={idx} className={`flex items-center gap-2.5 text-sm transition-all ${idx < aiStepIndex ? 'text-primary' : idx === aiStepIndex ? 'text-foreground font-semibold' : 'text-muted-foreground/40'}`}>
                              {idx < aiStepIndex ? <Check className="w-4 h-4 text-primary shrink-0" /> : idx === aiStepIndex ? <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" /> : <div className="w-4 h-4 rounded-full border border-border shrink-0" />}
                              {step.label}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </button>

                  {/* Option B: Manual */}
                  <button
                    onClick={() => setPhase('manual')}
                    className="w-full text-left p-5 rounded-2xl border-2 border-border hover:border-primary/30 hover:bg-muted/30 transition-all duration-200 active:scale-[0.99]"
                    data-testid="pai-option-manual"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center shrink-0">
                        <PenTool className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <p className="text-base font-bold text-foreground mb-1">Compila Manualmente</p>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          Procedi alla selezione manuale degli obiettivi e degli interventi assistenziali.
                        </p>
                      </div>
                    </div>
                  </button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     PHASE: PAI VIEW
     ════════════════════════════════════════════ */
  if (phase === 'pai') {
    return (
      <div className="flex flex-col h-[calc(100vh-3.5rem)]" data-testid="pai-phase">
        {/* Header */}
        <div className="bg-background/95 backdrop-blur-sm border-b border-border px-4 sm:px-6 py-4 shrink-0">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" className="shrink-0" onClick={() => setPhase('results')} data-testid="pai-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-foreground">Proposta Piano Assistenziale</h1>
              <p className="text-xs text-muted-foreground">{patient.cognome} {patient.nome} &middot; Generato con AI</p>
            </div>
            <Badge variant="chip" className="text-xs gap-1"><Sparkles className="w-3 h-3" /> Auto-generato</Badge>
            {isEditing && <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-0 text-xs font-bold gap-1 ml-2"><Edit3 className="w-3 h-3" /> In modifica</Badge>}
          </div>
        </div>

        {/* Scrollable PAI content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
          <div className="max-w-5xl mx-auto space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="md:col-span-3">
                <Card className="shadow-card h-full" data-testid="pai-body-map">
                  <CardHeader className="pb-2"><CardTitle className="text-base font-bold">Mappa Corporea</CardTitle><p className="text-xs text-muted-foreground">Tocca un'area per i dettagli</p></CardHeader>
                  <CardContent className="pt-2">
                    <WireframeBodyMap areas={generatedPAI?.bodyAreas || []} patient={patient} />
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="md:col-span-2 space-y-4">
                <Card className="shadow-card" data-testid="pai-objectives">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold flex items-center gap-2">
                      <Target className="w-5 h-5 text-primary" />Obiettivi di Salute
                      {isEditing && <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-0 text-[9px] font-bold ml-auto">Modifica</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2.5">
                      {(isEditing ? editObiettivi : generatedPAI?.obiettivi || []).map((obj, idx) => (
                        <motion.div key={obj.id} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + idx * 0.08 }} className={`p-3 rounded-xl border ${isEditing ? 'bg-amber-50/50 dark:bg-amber-950/10 border-amber-200/60 dark:border-amber-800/40' : 'bg-muted/50 border-border/50'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="chip">{obj.area}</Badge>
                            {isEditing ? (
                              <select
                                value={obj.priorita}
                                onChange={e => updateObiettivo(idx, 'priorita', e.target.value)}
                                className="text-[10px] font-bold px-2 py-0.5 rounded-md border border-border bg-background text-foreground"
                                data-testid={`edit-priorita-${idx}`}
                              >
                                <option value="alta">Alta</option>
                                <option value="media">Media</option>
                                <option value="bassa">Bassa</option>
                              </select>
                            ) : (
                              <Badge variant={obj.priorita === 'alta' ? 'alta' : 'media'} className="text-[10px]">{obj.priorita === 'alta' ? 'Alta' : 'Media'}</Badge>
                            )}
                          </div>
                          {isEditing ? (
                            <div className="space-y-1.5 mt-1">
                              <Input
                                value={obj.obiettivo}
                                onChange={e => updateObiettivo(idx, 'obiettivo', e.target.value)}
                                className="text-sm font-semibold h-8"
                                data-testid={`edit-obiettivo-${idx}`}
                              />
                              <Input
                                value={obj.indicatore}
                                onChange={e => updateObiettivo(idx, 'indicatore', e.target.value)}
                                className="text-xs h-7"
                                placeholder="Indicatore di risultato"
                                data-testid={`edit-indicatore-${idx}`}
                              />
                            </div>
                          ) : (
                            <>
                              <p className="text-sm font-semibold text-foreground">{obj.obiettivo}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{obj.indicatore}</p>
                            </>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                <Card className="shadow-card" data-testid="pai-prestazioni">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-bold flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-primary" />Prestazioni Suggerite
                      {isEditing && <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-0 text-[9px] font-bold ml-auto">Modifica</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2.5">
                      {(isEditing ? editPrestazioni : generatedPAI?.prestazioni || []).map((prest, idx) => (
                        <motion.div key={prest.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5 + idx * 0.06 }} className={`flex items-start gap-3 p-3 rounded-xl border ${isEditing ? 'bg-amber-50/50 dark:bg-amber-950/10 border-amber-200/60 dark:border-amber-800/40' : 'bg-muted/50 border-border/50'}`}>
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Stethoscope className="w-5 h-5 text-primary" /></div>
                          {isEditing ? (
                            <div className="flex-1 space-y-2">
                              <p className="text-sm font-bold text-foreground">{prest.tipo}</p>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Frequenza</label>
                                  <Input
                                    value={prest.frequenza}
                                    onChange={e => updatePrestazione(idx, 'frequenza', e.target.value)}
                                    className="text-xs h-8 mt-0.5"
                                    data-testid={`edit-frequenza-${idx}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Durata</label>
                                  <Input
                                    value={prest.durata}
                                    onChange={e => updatePrestazione(idx, 'durata', e.target.value)}
                                    className="text-xs h-8 mt-0.5"
                                    data-testid={`edit-durata-${idx}`}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Note</label>
                                <Textarea
                                  value={prest.note}
                                  onChange={e => updatePrestazione(idx, 'note', e.target.value)}
                                  className="text-xs mt-0.5 min-h-[48px]"
                                  rows={2}
                                  data-testid={`edit-note-${idx}`}
                                />
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="text-sm font-bold text-foreground">{prest.tipo}</p>
                              <p className="text-xs text-muted-foreground">{prest.frequenza} &middot; {prest.durata}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{prest.note}</p>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <Card className="shadow-card">
              <CardContent className="p-5">
                <label className="text-sm font-bold text-foreground block mb-2">Note aggiuntive al PAI</label>
                <Textarea placeholder="Inserisci eventuali note o modifiche al piano..." value={notesPAI} onChange={(e) => setNotesPAI(e.target.value)} rows={3} className="text-base" data-testid="pai-notes-textarea" />
              </CardContent>
            </Card>

            <Card className="shadow-card border-primary/20" data-testid="pai-actions-card">
              <CardContent className="p-5 space-y-3">
                {!isEditing ? (
                  <>
                    <Button variant="comfort" size="lg" className="w-full gap-2 h-14 text-base font-bold" onClick={handleSign} disabled={isSigning} data-testid="firma-digitale-btn">
                      {isSigning ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileSignature className="w-5 h-5" />}
                      {isSigning ? 'Firma in corso...' : 'Approva e Valida PAI'}
                    </Button>
                    <Button variant="outline" size="lg" className="w-full gap-2 h-12 text-base" onClick={enterEditMode} disabled={isSigning} data-testid="modifica-manuale-btn">
                      <Edit3 className="w-4 h-4" /> Modifica manualmente
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="lg" className="w-full gap-2 h-14 text-base font-bold bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveEdits} data-testid="salva-modifiche-btn">
                      <Save className="w-5 h-5" /> Salva Modifiche
                    </Button>
                    <Button variant="outline" size="lg" className="w-full gap-2 h-12 text-base" onClick={cancelEdit} data-testid="annulla-modifiche-btn">
                      <XCircle className="w-4 h-4" /> Annulla
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     PHASE: QUESTIONS — Split Wizard Layout
     ════════════════════════════════════════════ */
  const currentValue = question ? formData[`${section.id}_${question.id}`] : undefined;
  const hasError = showErrors && currentValue === undefined;
  const getCols = (n) => n <= 2 ? 'grid-cols-2' : n === 4 ? 'grid-cols-2' : 'grid-cols-3';

  return (
    <div className="flex h-[calc(100vh-3.5rem)]" data-testid="evaluation-wizard">

      {/* ═══ LEFT SIDEBAR — Section Stepper ═══ */}
      <div className="w-[280px] lg:w-[300px] bg-stone-50/80 dark:bg-muted/30 border-r border-border shrink-0 flex flex-col overflow-hidden" data-testid="wizard-sidebar">
        {/* Sidebar header */}
        <div className="px-5 pt-5 pb-3 border-b border-border/50">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-primary">{patient.cognome?.[0]}{patient.nome?.[0]}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-foreground truncate">{patient.cognome} {patient.nome}</p>
              <p className="text-[10px] text-muted-foreground">{patient.eta} anni</p>
            </div>
          </div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Valutazione interRAI HC</p>
        </div>

        {/* Stepper */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="relative">
            {interraiSteps.map((sec, si) => {
              const answered = sectionAnswered(si);
              const total = sec.domande.length;
              const isCompleted = answered === total;
              const isActive = si === currentSectionIdx;
              const Icon = iconMap[sec.icon];
              return (
                <div key={sec.id} className="relative flex items-start gap-3.5 group" data-testid={`sidebar-section-${si}`}>
                  {/* Vertical line */}
                  {si < totalSections - 1 && (
                    <div className={`absolute left-[15px] top-[36px] w-[2px] h-[calc(100%-4px)] transition-colors ${isCompleted ? 'bg-primary' : 'bg-border'}`} />
                  )}
                  {/* Dot / Icon */}
                  <button
                    onClick={() => { setCurrentSectionIdx(si); setCurrentQuestionIdx(0); }}
                    className={`relative z-10 w-[32px] h-[32px] rounded-full flex items-center justify-center shrink-0 border-2 transition-all duration-300 cursor-pointer
                      ${isCompleted ? 'bg-primary border-primary' : isActive ? 'bg-card border-primary shadow-md' : 'bg-card border-border hover:border-primary/40'}`}
                    data-testid={`sidebar-dot-${si}`}
                  >
                    {isCompleted ? (
                      <Check className="w-4 h-4 text-primary-foreground" />
                    ) : Icon ? (
                      <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                    ) : (
                      <span className={`text-xs font-bold ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>{si + 1}</span>
                    )}
                  </button>
                  {/* Label & sub-label */}
                  <button
                    onClick={() => { setCurrentSectionIdx(si); setCurrentQuestionIdx(0); }}
                    className="text-left pb-7 min-w-0 cursor-pointer"
                  >
                    <p className={`text-[13px] leading-tight transition-colors ${isActive ? 'font-bold text-foreground' : isCompleted ? 'font-semibold text-foreground/80' : 'font-medium text-muted-foreground'}`}>
                      {wizardStepsMeta[si]?.label || sec.titolo}
                    </p>
                    <p className="text-[10px] text-muted-foreground/70 mt-0.5 leading-snug line-clamp-2">{sec.sottotitolo}</p>
                    <Badge variant={isCompleted ? 'default' : 'outline'} className={`text-[9px] mt-1.5 ${isCompleted ? 'bg-primary/10 text-primary border-primary/20' : ''}`}>
                      {answered}/{total}
                    </Badge>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar footer: progress */}
        <div className="px-5 py-3 border-t border-border/50">
          <div className="flex items-center justify-between text-[10px] font-semibold text-muted-foreground mb-1.5">
            <span>Progresso globale</span>
            <span>{Object.keys(formData).length}/{totalQuestionsAll}</span>
          </div>
          <Progress value={(Object.keys(formData).length / totalQuestionsAll) * 100} className="h-1.5" />
        </div>
      </div>

      {/* ═══ RIGHT AREA — Question + Card Grid ═══ */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="px-6 lg:px-10 py-4 border-b border-border/50 bg-background shrink-0 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              {section?.titolo}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">Domanda {currentQuestionIdx + 1} di {section?.domande.length} in questa sezione</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="chip" className="text-[10px]">
              {flatIdx + 1}/{totalQuestionsAll}
            </Badge>
            <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs font-semibold border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/20" onClick={handleSaveDraftQuick} data-testid="wizard-save-draft-btn">
              <Save className="w-3 h-3" /> Salva Bozza
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/5 h-8 text-xs" onClick={() => setShowInterruptConfirm(true)} data-testid="wizard-interrupt-btn">
              <X className="w-3 h-3" /> Interrompi
            </Button>
          </div>
        </div>

        {/* Question content (centered, spacious) */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${currentSectionIdx}-${currentQuestionIdx}`}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40, pointerEvents: 'none' }}
              transition={{ duration: 0.22 }}
              className="max-w-3xl mx-auto px-6 lg:px-10 py-8 lg:py-12"
            >
              {question && (
                <div data-testid={`question-${question.id}`}>
                  {/* Question header */}
                  <div className="flex items-start gap-3 mb-2">
                    <Badge variant="outline" className={`text-xs font-mono shrink-0 mt-0.5 px-2.5 py-1 ${hasError ? 'border-destructive text-destructive' : ''}`}>{question.codice}</Badge>
                    <div className="flex items-center gap-3 flex-1">
                      <h2 className="text-xl lg:text-2xl font-bold text-foreground leading-snug">{question.label}</h2>
                      <span className="text-3xl font-bold text-muted-foreground/20 shrink-0 tabular-nums">{String(flatIdx + 1).padStart(2, '0')}</span>
                    </div>
                  </div>
                  {question.istruzione && (
                    <p className="text-sm text-muted-foreground italic ml-0 mt-1 mb-6">{question.istruzione}</p>
                  )}
                  {hasError && <p className="text-xs text-destructive font-semibold mb-4" data-testid={`error-${question.id}`}>Seleziona una risposta per continuare</p>}

                  {/* Card grid options */}
                  <div className={`grid ${getCols(question.opzioni.length)} gap-3 lg:gap-4 mt-4`}>
                    {question.opzioni.map((opt) => {
                      const isSelected = currentValue === opt.value;
                      const v = parseInt(opt.value);
                      const optCount = question.opzioni.length;
                      // Severity color mapping
                      let sevColor = '#6366F1'; // default indigo
                      let sevBg = '#6366F110';
                      if (!isNaN(v) && v !== 8) {
                        if (v === 0) { sevColor = '#22C55E'; sevBg = '#22C55E'; }
                        else if (optCount <= 3 && v === 1) { sevColor = '#F59E0B'; sevBg = '#F59E0B'; }
                        else if (optCount <= 3 && v >= 2) { sevColor = '#EF4444'; sevBg = '#EF4444'; }
                        else {
                          const ratio = v / (optCount - 1);
                          if (ratio <= 0.25) { sevColor = '#22C55E'; sevBg = '#22C55E'; }
                          else if (ratio <= 0.5) { sevColor = '#F59E0B'; sevBg = '#F59E0B'; }
                          else if (ratio <= 0.75) { sevColor = '#F97316'; sevBg = '#F97316'; }
                          else { sevColor = '#EF4444'; sevBg = '#EF4444'; }
                        }
                      }

                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleAnswer(section.id, question.id, opt.value)}
                          data-testid={`option-${opt.value}`}
                          className={`relative text-left rounded-2xl transition-all duration-200 active:scale-[0.97] group cursor-pointer overflow-hidden
                            ${isSelected
                              ? 'shadow-lg'
                              : 'bg-card hover:shadow-md'
                            }`}
                          style={isSelected
                            ? {
                                borderTop: `4px solid ${sevColor}`,
                                borderRight: `2px solid ${sevColor}`,
                                borderBottom: `2px solid ${sevColor}`,
                                borderLeft: `2px solid ${sevColor}`,
                                backgroundColor: `${sevBg}08`,
                                boxShadow: `0 4px 20px ${sevColor}20`,
                              }
                            : {
                                borderTop: `4px solid ${sevColor}80`,
                                borderRight: '2px solid hsl(var(--border) / 0.5)',
                                borderBottom: '2px solid hsl(var(--border) / 0.5)',
                                borderLeft: '2px solid hsl(var(--border) / 0.5)',
                              }
                          }
                        >
                          <div className="p-4 lg:p-5">
                            {/* Checkmark badge */}
                            {isSelected && (
                              <div className="absolute top-3.5 right-2.5 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: sevColor }}>
                                <Check className="w-3.5 h-3.5 text-white" />
                              </div>
                            )}
                            {/* Value circle — colored by severity */}
                            <div
                              className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-colors"
                              style={{ backgroundColor: `${sevBg}${isSelected ? '20' : '12'}` }}
                            >
                              <span className="text-lg font-bold" style={{ color: sevColor }}>
                                {opt.value}
                              </span>
                            </div>
                            <p className={`text-sm font-bold leading-tight ${isSelected ? 'text-foreground' : 'text-foreground/80'}`}>{opt.label}</p>
                            <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{opt.desc}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom navigation */}
        <div className="px-6 lg:px-10 py-4 border-t border-border/50 bg-background shrink-0" data-testid="wizard-footer">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            {flatIdx > 0 ? (
              <Button variant="outline" size="lg" onClick={goPrevQuestion} className="gap-2 h-11 text-sm" data-testid="wizard-prev-btn">
                <ArrowLeft className="w-4 h-4" /> Indietro
              </Button>
            ) : <div />}
            <Button
              variant="comfort"
              size="lg"
              onClick={handleNext}
              className={`gap-2 h-11 text-sm font-bold ${isVeryLast ? 'px-6' : ''}`}
              data-testid="wizard-next-btn"
            >
              {isVeryLast ? <><Flag className="w-4 h-4" /> Termina Valutazione</> : <>Avanti <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </div>
        </div>
      </div>

      {/* ════ Interrupt Modal ════ */}
      <AnimatePresence>
        {showInterruptConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="interrupt-modal">
            <div className="absolute inset-0 bg-foreground/20 backdrop-blur-[2px]" onClick={() => setShowInterruptConfirm(false)} />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="relative w-full max-w-sm bg-card rounded-2xl shadow-elevated p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-3"><AlertTriangle className="w-6 h-6 text-destructive" /></div>
              <h3 className="text-base font-bold text-foreground mb-1">Interrompere la valutazione?</h3>
              <p className="text-sm text-muted-foreground mb-5">I dati inseriti saranno salvati come bozza.</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 h-11" onClick={() => setShowInterruptConfirm(false)} data-testid="interrupt-cancel-btn">Annulla</Button>
                <Button variant="destructive" className="flex-1 h-11 font-bold" onClick={handleInterrupt} data-testid="interrupt-confirm-btn">Interrompi</Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
